import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  getAllDocsSnap,
  setDocData,
  storage,
  deleteDocById,
} from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import Select from "react-select";
import {
  FiUpload,
  FiFileText,
  FiCalendar,
  FiUser,
  FiCheck,
  FiX,
  FiClock,
  FiPlus,
  FiFilter,
  FiTrendingUp,
  FiPieChart,
  FiBarChart2,
  FiDownload,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiDollarSign,
  FiMessageSquare,
  FiCornerUpLeft,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { FaMoneyBillWave } from "react-icons/fa";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-custom.css";

function Expenses() {
  const currentUser = useSelector((state) => state.userObject);
  const [activeTab, setActiveTab] = useState("requests");
  const [activeFilter, setActiveFilter] = useState("all");
  const [requests, setRequests] = useState([]);
  const [approvers, setApprovers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeUpload, setActiveUpload] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [comment, setComment] = useState("");
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [resubmitComment, setResubmitComment] = useState("");
  const expenseCategories = [
    { value: "security", label: "Security Expenses" },
    { value: "salary", label: "Salary & Wages" },
    { value: "maintenance", label: "Maintenance & Repairs" },
    { value: "utilities", label: "Utilities" },
    { value: "supplies", label: "Office & Maintenance Supplies" },
    { value: "insurance", label: "Insurance" },
    { value: "taxes", label: "Property Taxes" },
    { value: "cleaning", label: "Cleaning Services" },
    { value: "landscaping", label: "Landscaping & Gardening" },
    { value: "equipment", label: "Equipment & Tools" },
    { value: "legal", label: "Legal & Professional Fees" },
    { value: "marketing", label: "Marketing & Advertising" },
    { value: "training", label: "Staff Training & Development" },
    { value: "emergency", label: "Emergency Repairs" },
    { value: "capital", label: "Capital Improvements" },
    { value: "other", label: "Other Expenses" },
  ];

  const [formData, setFormData] = useState({
    date: "",
    supplierName: "",
    amount: "",
    vat: "",
    description: "",
    approver: null,
    invoiceFiles: [],
    status: "pending",
    category: null,
  });

  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const columns = [
    { field: "date", headerName: "Date", width: 150 },
    { field: "supplierName", headerName: "Supplier", width: 200 },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      renderCell: (params) => {
        const category = params.row.category;
        if (!category) return "Uncategorized";
        return category.label || "Uncategorized";
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
      renderCell: (params) => {
        const amount = params.value || 0;
        return (
          <div className="flex items-center gap-2">
            <FaMoneyBillWave className="text-[#363062]" />
            <span>₱{amount.toLocaleString()}</span>
          </div>
        );
      },
    },
    {
      field: "vat",
      headerName: "VAT",
      width: 130,
      renderCell: (params) => {
        const vat = params.value || 0;
        return (
          <div className="flex items-center gap-2">
            <FaMoneyBillWave className="text-[#363062]" />
            <span>₱{vat.toLocaleString()}</span>
          </div>
        );
      },
    },
    { field: "description", headerName: "Description", width: 200 },
    {
      field: "approver",
      headerName: "Approver",
      width: 150,
      renderCell: (params) => {
        const approver = params.row.approver;
        if (!approver) return "Not assigned";
        return approver.label || "Not assigned";
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        return getStatusBadge(params.value);
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        return (
          <div
            className="flex items-center justify-center gap-3"
            style={{ width: "100%", height: "100%" }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewRequest(params.row);
              }}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#363062]"
              title="View details"
            >
              <FiEye className="w-4 h-4" />
            </button>
            {params.row.status === "pending" && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditRequest(params.row);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#363062]"
                  title="Edit request"
                >
                  <FiEdit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteRequest(params.row);
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-red-500"
                  title="Delete request"
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      // Fetch all payment requests
      await getAllDocsSnap("paymentRequests", (data) => {
        setRequests(data);
        setIsLoading(false);
      });

      // Fetch users with approver role
      await getAllDocsSnap("users", (users) => {
        const approverUsers = users.filter((user) =>
          user.roles?.includes("approver")
        );
        setApprovers(
          approverUsers.map((user) => ({
            value: user.email,
            label: user.displayName,
          }))
        );
      });
    };
    fetchData();
  }, []);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setActiveUpload("invoice");
    const uploadPromises = files.map((file) => {
      const storageRef = ref(storage, `invoices/${v4()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setUploadProgress(progress);
          },
          (error) => {
            toast.error(`Error uploading ${file.name}`);
            reject(error);
          },
          () => {
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
              resolve({
                name: file.name,
                url: downloadURL,
                type: file.type,
                size: file.size,
              });
            });
          }
        );
      });
    });

    Promise.all(uploadPromises)
      .then((uploadedFiles) => {
        setUploadedFiles((prev) => [...prev, ...uploadedFiles]);
        setFormData((prev) => ({
          ...prev,
          invoiceFiles: [
            ...prev.invoiceFiles,
            ...uploadedFiles.map((f) => f.url),
          ],
        }));
        toast.success("Files uploaded successfully");
        setUploadProgress(0);
      })
      .catch((error) => {
        console.error("Error uploading files:", error);
        toast.error("Error uploading some files");
      });
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setFormData((prev) => ({
      ...prev,
      invoiceFiles: prev.invoiceFiles.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.invoiceFiles.length === 0) {
      toast.error("Please upload at least one invoice");
      return;
    }

    const requestData = {
      ...formData,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.email,
      createdByName: currentUser.displayName,
    };

    try {
      if (selectedRequest) {
        // Update existing request
        await setDocData("paymentRequests", selectedRequest.id, requestData);
        toast.success("Payment request updated successfully");
      } else {
        // Create new request
        const newRequestId = v4();
        await setDocData("paymentRequests", newRequestId, {
          ...requestData,
          id: newRequestId,
        });
        toast.success("Payment request submitted successfully");
      }

      setShowNewRequestModal(false);
      setSelectedRequest(null);
      setUploadedFiles([]);
      setFormData({
        date: "",
        supplierName: "",
        amount: "",
        vat: "",
        description: "",
        approver: null,
        invoiceFiles: [],
        status: "pending",
        category: null,
      });
    } catch (error) {
      console.error("Error saving payment request:", error);
      toast.error("Error saving payment request");
    }
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: "bg-[#363062]/10 text-[#363062]",
      approved: "bg-[#8FCD00]/10 text-[#8FCD00]",
      rejected: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyles[status]}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getCommentTypeBadge = (type) => {
    const typeStyles = {
      user: "bg-[#363062]/10 text-[#363062]",
      approved: "bg-[#8FCD00]/10 text-[#8FCD00]",
      rejected: "bg-red-100 text-red-800",
      resubmitted: "bg-purple-100 text-purple-800",
    };

    const typeLabels = {
      user: "Comment",
      approved: "Approved",
      rejected: "Rejected",
      resubmitted: "Resubmitted",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${typeStyles[type]}`}
      >
        {typeLabels[type]}
      </span>
    );
  };

  const handleExcelDownload = () => {
    // Get the filtered requests based on current filters
    const dataToExport = filteredRequests;

    // Convert filtered requests data to CSV format
    const headers = [
      "Date",
      "Supplier",
      "Category",
      "Amount",
      "VAT",
      "Description",
      "Approver",
      "Status",
    ];

    const csvData = dataToExport.map((request) => [
      new Date(request.date).toLocaleDateString(),
      request.supplierName,
      request.category?.label || "Uncategorized",
      request.amount,
      request.vat,
      request.description,
      request.approver?.label || "Not assigned",
      request.status,
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    // Add date range to filename if filter is applied
    let filename = "expenses";
    if (startDate && endDate) {
      const startStr = startDate.toLocaleDateString().replace(/\//g, "-");
      const endStr = endDate.toLocaleDateString().replace(/\//g, "-");
      filename += `_${startStr}_to_${endStr}`;
    }

    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredRequests = requests.filter((request) => {
    // Filter by date range
    if (startDate && endDate) {
      const requestDate = new Date(request.date);

      // Set time to start of day for request date
      requestDate.setHours(0, 0, 0, 0);

      // Create copies of start and end dates
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Set time to start of day for start date and end of day for end date
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      // Check if request date is within the range (inclusive)
      if (requestDate < start || requestDate > end) {
        return false;
      }
    }

    // Filter by status
    if (activeFilter !== "all") {
      return request.status === activeFilter;
    }

    return true;
  });

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setShowViewModal(true);
  };

  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setFormData({
      date: request.date,
      supplierName: request.supplierName,
      amount: request.amount,
      vat: request.vat,
      description: request.description,
      approver: request.approver,
      invoiceFiles: request.invoiceFiles || [],
      status: request.status,
      category: request.category,
    });
    setShowNewRequestModal(true);
  };

  const handleDeleteRequest = async (request) => {
    if (request.status !== "pending") {
      toast.error("Only pending requests can be deleted");
      return;
    }

    if (window.confirm("Are you sure you want to delete this request?")) {
      try {
        await deleteDocById("paymentRequests", request.id);
        toast.success("Request deleted successfully");
      } catch (error) {
        console.error("Error deleting request:", error);
        toast.error("Error deleting request");
      }
    }
  };

  const handleAddComment = async (request) => {
    if (!selectedRequest) return;

    const commentData = {
      text: comment,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.email,
      createdByName: currentUser.displayName,
      type: "user",
    };

    try {
      const updatedRequest = {
        ...selectedRequest,
        comments: [...(selectedRequest.comments || []), commentData],
      };

      await setDocData("paymentRequests", selectedRequest.id, updatedRequest);
      // Update the local state to reflect the changes immediately
      setRequests(
        requests.map((req) =>
          req.id === selectedRequest.id ? updatedRequest : req
        )
      );
      setSelectedRequest(updatedRequest);
      toast.success("Comment added successfully");
      setComment("");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Error adding comment");
    }
  };

  const handleResubmit = async (request) => {
    if (!selectedRequest) return;

    const resubmitData = {
      ...selectedRequest,
      status: "pending",
      comments: [
        ...(selectedRequest.comments || []),
        {
          text: "Request resubmitted for approval",
          createdAt: new Date().toISOString(),
          createdBy: currentUser.email,
          createdByName: currentUser.displayName,
          type: "resubmitted",
        },
      ],
    };

    try {
      await setDocData("paymentRequests", selectedRequest.id, resubmitData);
      // Update the local state to reflect the changes immediately
      setRequests(
        requests.map((req) =>
          req.id === selectedRequest.id ? resubmitData : req
        )
      );
      setSelectedRequest(resubmitData);
      toast.success("Request resubmitted successfully");
      setShowResubmitModal(false);
    } catch (error) {
      console.error("Error resubmitting request:", error);
      toast.error("Error resubmitting request");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-lg font-semibold text-[#363062]">
            Expenses Management
          </h1>
          <p className="text-sm text-gray-500">
            Track and manage payment requests
          </p>
        </div>
        <button
          onClick={() => setShowNewRequestModal(true)}
          className="flex items-center gap-2 bg-[#363062] text-white px-4 py-2 rounded-lg hover:bg-[#363062]/90 transition-colors"
        >
          <FiPlus className="w-4 h-4" />
          New Request
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-50 rounded-lg">
              <FiDollarSign className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Expenses</div>
              <div className="text-2xl font-semibold text-gray-900">
                ₱
                {requests
                  .filter((req) => req.status === "approved")
                  .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0)
                  .toLocaleString()}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#8FCD00]/10 rounded-lg">
              <FiCheckCircle className="w-5 h-5 text-[#8FCD00]" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Approved</div>
              <div className="text-2xl font-semibold text-[#8FCD00]">
                {requests.filter((req) => req.status === "approved").length}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#363062]/10 rounded-lg">
              <FiClock className="w-5 h-5 text-[#363062]" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Pending</div>
              <div className="text-2xl font-semibold text-[#363062]">
                {requests.filter((req) => req.status === "pending").length}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 rounded-lg">
              <FiAlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Rejected</div>
              <div className="text-2xl font-semibold text-red-500">
                {requests.filter((req) => req.status === "rejected").length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="flex items-center gap-4 flex-wrap mb-6">
        <div className="flex items-center gap-2 relative datepicker-wrapper">
          <div className="absolute left-3 z-10 text-[#363062]">
            <FiCalendar className="w-4 h-4" />
          </div>
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-[#363062] font-poppins text-sm w-72"
            placeholderText="Filter by date range"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setActiveFilter("all")}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              activeFilter === "all"
                ? "bg-[#363062] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setActiveFilter("pending")}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              activeFilter === "pending"
                ? "bg-[#363062] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setActiveFilter("approved")}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              activeFilter === "approved"
                ? "bg-[#363062] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Approved
          </button>
          <button
            onClick={() => setActiveFilter("rejected")}
            className={`px-3 py-1.5 rounded-lg text-sm ${
              activeFilter === "rejected"
                ? "bg-[#363062] text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            Rejected
          </button>
        </div>

        <button
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs inline-flex items-center gap-2"
          onClick={handleExcelDownload}
        >
          <FiDownload className="w-4 h-4" />
          <span>Download Excel</span>
        </button>
      </div>

      {/* DataGrid Table */}
      <Paper sx={{ height: "70vh", width: "100%", marginTop: "1rem" }}>
        <DataGrid
          rows={filteredRequests}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
            sorting: {
              sortModel: [{ field: "date", sort: "desc" }],
            },
          }}
          pageSizeOptions={[5, 10, 25]}
          sx={{
            border: 0,
            fontSize: "0.8rem",
            fontFamily: "inherit",
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
          onRowClick={(params) => {
            handleViewRequest(params.row);
          }}
        />
      </Paper>

      {showNewRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#363062]">
                {selectedRequest
                  ? "Edit Payment Request"
                  : "New Payment Request"}
              </h2>
              <button
                onClick={() => {
                  setShowNewRequestModal(false);
                  setSelectedRequest(null);
                  setUploadedFiles([]);
                  setFormData({
                    date: "",
                    supplierName: "",
                    amount: "",
                    vat: "",
                    description: "",
                    approver: null,
                    invoiceFiles: [],
                    status: "pending",
                    category: null,
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Name
                  </label>
                  <input
                    type="text"
                    value={formData.supplierName}
                    onChange={(e) =>
                      setFormData({ ...formData, supplierName: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaMoneyBillWave className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      value={formData.amount}
                      onChange={(e) =>
                        setFormData({ ...formData, amount: e.target.value })
                      }
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#363062] focus:border-[#363062] sm:text-sm"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaMoneyBillWave className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="number"
                      value={formData.vat}
                      onChange={(e) =>
                        setFormData({ ...formData, vat: e.target.value })
                      }
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-[#363062] focus:border-[#363062] sm:text-sm"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                    rows="3"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approver
                  </label>
                  <Select
                    options={approvers}
                    value={formData.approver}
                    onChange={(selected) =>
                      setFormData({ ...formData, approver: selected })
                    }
                    className="w-full"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <Select
                    options={expenseCategories}
                    value={formData.category}
                    onChange={(selected) =>
                      setFormData({ ...formData, category: selected })
                    }
                    className="w-full"
                    placeholder="Select expense category"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoices
                  </label>
                  <div className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center hover:border-[#363062]/50 transition-colors">
                    <input
                      type="file"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="invoice-upload"
                      accept=".pdf,.jpg,.jpeg,.png"
                      multiple
                    />
                    <label
                      htmlFor="invoice-upload"
                      className="cursor-pointer block"
                    >
                      <FiUpload className="w-8 h-8 text-[#363062]/50 mx-auto mb-2" />
                      <div className="text-sm text-gray-600">
                        Click to upload or drag and drop multiple files
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Supported formats: PDF, JPG, JPEG, PNG
                      </div>
                      {uploadProgress > 0 && (
                        <div className="mt-2">
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#363062] transition-all duration-300"
                              style={{ width: `${uploadProgress}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Uploading... {Math.round(uploadProgress)}%
                          </div>
                        </div>
                      )}
                    </label>
                  </div>

                  {uploadedFiles.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <h3 className="text-sm font-medium text-gray-700">
                        Uploaded Files
                      </h3>
                      <div className="space-y-2">
                        {uploadedFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <FiFileText className="w-4 h-4 text-[#363062]" />
                              <span className="text-sm text-gray-900 truncate max-w-xs">
                                {file.name}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#363062] hover:text-[#363062]/80"
                              >
                                <FiEye className="w-4 h-4" />
                              </a>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(index)}
                                className="text-red-500 hover:text-red-600"
                              >
                                <FiX className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={() => setShowNewRequestModal(false)}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-[#363062]/90"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#363062]">
                Payment Request Details
              </h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setComment("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <p className="text-sm text-gray-900">
                    {new Date(selectedRequest.date).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Supplier Name
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.supplierName}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="flex items-center gap-2">
                    <FaMoneyBillWave className="text-[#363062]" />
                    <p className="text-sm text-gray-900">
                      ₱{selectedRequest.amount}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    VAT
                  </label>
                  <div className="flex items-center gap-2">
                    <FaMoneyBillWave className="text-[#363062]" />
                    <p className="text-sm text-gray-900">
                      ₱{selectedRequest.vat}
                    </p>
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.description}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approver
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.approver?.label || "Not assigned"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  {getStatusBadge(selectedRequest.status)}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <p className="text-sm text-gray-900">
                    {selectedRequest.category?.label || "Uncategorized"}
                  </p>
                </div>
                {selectedRequest.invoiceFiles &&
                  selectedRequest.invoiceFiles.length > 0 && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Invoices
                      </label>
                      <div className="space-y-2">
                        {selectedRequest.invoiceFiles.map((file, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between bg-gray-50 p-2 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <FiFileText className="w-4 h-4 text-[#363062]" />
                              <span className="text-sm text-gray-900 truncate max-w-xs">
                                {file}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#363062] hover:text-[#363062]/80"
                              >
                                <FiEye className="w-4 h-4" />
                              </a>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>

              {/* Comments History */}
              {selectedRequest.comments &&
                selectedRequest.comments.length > 0 && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Comments History
                    </label>
                    <div className="space-y-3">
                      {selectedRequest.comments.map((comment, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 p-3 rounded-lg space-y-2"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900">
                                {comment.createdByName}
                              </span>
                              {getCommentTypeBadge(comment.type)}
                            </div>
                            <span className="text-xs text-gray-500">
                              {new Date(comment.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700">
                            {comment.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {/* Add Comment Section */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add Comment
                </label>
                <div className="flex gap-2">
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                    rows="2"
                    placeholder="Add your comment here..."
                  />
                  <button
                    onClick={() => handleAddComment(selectedRequest)}
                    className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-[#363062]/90 self-end"
                  >
                    <FiMessageSquare className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Resubmit Button for Rejected Requests */}
              {selectedRequest.status === "rejected" && (
                <div className="col-span-2 flex justify-end">
                  <button
                    onClick={() => handleResubmit(selectedRequest)}
                    className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-[#363062]/90 flex items-center gap-2"
                  >
                    <FiCornerUpLeft className="w-4 h-4" />
                    Resubmit for Approval
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resubmit Modal */}
      {showResubmitModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#363062]">
                Resubmit Request
              </h2>
              <button
                onClick={() => {
                  setShowResubmitModal(false);
                  setResubmitComment("");
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response to Previous Comments
                </label>
                <textarea
                  value={resubmitComment}
                  onChange={(e) => setResubmitComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#363062]/20 focus:border-[#363062]"
                  rows="3"
                  placeholder="Add your response here..."
                />
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  onClick={() => {
                    setShowResubmitModal(false);
                    setResubmitComment("");
                  }}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleResubmit(selectedRequest)}
                  className="px-4 py-2 bg-[#363062] text-white rounded-lg hover:bg-[#363062]/90"
                >
                  Resubmit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Expenses;
